import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase_client';
import { isReloginGuardActive } from '../utils/reloginModalBus';
import { ensureFreshSession, isSessionFresh, recoverAfterSignedOut } from '../utils/sessionRecovery';
import type { Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Latest user, readable inside the auth listener without re-subscribing.
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  // Distinguishes the user clicking "Sign out" (redirect to /signin) from a
  // transient / unexpected SIGNED_OUT (must NOT kick the user out).
  const intentionalSignOutRef = useRef(false);

  // Prevent overlapping recovery attempts from a burst of SIGNED_OUT events.
  const recoveringRef = useRef(false);

  /** Full local cleanup + redirect — ONLY for intentional sign-out. */
  const performSignOutRedirect = () => {
    setUser(null);
    setSession(null);
    localStorage.removeItem('selectedCompany');
    if (window.location.pathname !== '/signin') {
      window.location.href = '/signin';
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Get initial session
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    /**
     * Quiet keepalive when the tab becomes visible again.
     * Uses the shared mutex so we never race supabase auto-refresh / Save.
     * Does NOT open modals or redirect.
     */
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (isReloginGuardActive() || intentionalSignOutRef.current) return;

      void (async () => {
        try {
          const { data: { session: current } } = await supabase.auth.getSession();
          // Only nudge when the JWT is near expiry (or missing while UI still
          // thinks we are signed in). Healthy tokens are left alone.
          if (!current && userRef.current) {
            await ensureFreshSession(60_000);
            return;
          }
          if (current && !isSessionFresh(current, 120_000)) {
            await ensureFreshSession(60_000);
          }
        } catch (e) {
          console.warn('[Auth] visibility keepalive failed:', e);
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // During EditFilings in-place re-login, ignore auth events so the form
      // never remounts / navigates (unsaved DOM values would be lost).
      if (isReloginGuardActive()) {
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_OUT') {
        const wasIntentional = intentionalSignOutRef.current;
        intentionalSignOutRef.current = false;

        if (wasIntentional) {
          performSignOutRedirect();
          setLoading(false);
          return;
        }

        // ── Unexpected SIGNED_OUT ──────────────────────────────────────────
        // This is the bug class behind "loaded a while then logged out":
        // refresh-token races, multi-tab reuse detection, brief network blips.
        //
        // Forever-login policy:
        //  • NEVER hard-redirect to /signin
        //  • NEVER pop the Session Expired modal from here
        //  • Try silent recovery; if it fails, keep the existing React user so
        //    ProtectedRoute does not bounce the page. EditFilings Save still
        //    has its own in-place re-login if a write truly needs a new session.
        setLoading(false);

        if (recoveringRef.current) return;
        recoveringRef.current = true;

        // Defer auth API calls — calling getSession/refresh inside this
        // callback can deadlock on supabase-js's navigator lock.
        setTimeout(() => {
          void (async () => {
            try {
              const recovered = await recoverAfterSignedOut();
              if (cancelled) return;

              if (recovered) {
                setSession(recovered);
                setUser(recovered.user ?? null);
                return;
              }

              // Still nothing in storage — try one serialized refresh in case
              // tokens were mid-write when SIGNED_OUT fired.
              const ok = await ensureFreshSession(10_000);
              if (cancelled) return;
              if (ok) {
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                  setSession(data.session);
                  setUser(data.session.user ?? null);
                  return;
                }
              }

              // True loss of server session. Keep UI auth state as-is so the
              // user is not yanked to /signin mid-work. Next authenticated
              // write (e.g. EditFilings Save) will recover in place if needed.
              console.warn(
                '[Auth] Unexpected SIGNED_OUT could not be recovered silently; keeping UI session mounted'
              );
            } catch (e) {
              console.warn('[Auth] Unexpected SIGNED_OUT recovery error:', e);
            } finally {
              recoveringRef.current = false;
            }
          })();
        }, 0);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const signOut = async () => {
    // User-initiated: the SIGNED_OUT event should redirect, not recover.
    intentionalSignOutRef.current = true;

    // Tell Supabase, but NEVER let this call block the logout: it can hang on
    // its internal navigator.locks (e.g. a stale tab or an interrupted token
    // refresh holds the lock) or fail on network/expired-token errors.
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: 'local' }),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    } catch (error) {
      console.warn('Sign out API call failed, cleaning up locally:', error);
    }

    // Always clean up locally and leave, regardless of what the call above did.
    try {
      Object.keys(localStorage).forEach((key) => {
        // Selective: remove only Supabase auth tokens (preserve Chatwoot etc.)
        if (key.startsWith('sb-') && key.includes('auth-token')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
    performSignOutRedirect();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
